import React, {useCallback, useMemo, useRef, useState} from 'react';
import clsx from 'clsx';
import {
	// DialogContent,
	DialogContentText,
	LinearProgress,
	Paper,
} from '@mui/material';
import {createStyles, withStyles, type WithStyles} from '@mui/styles';
import {useConfirmation} from '@netx/core-components/Confirmation';
import {Dialog} from '@netx/core-components/Dialog';
import {FormActions, FormValues} from '@netx/core-components/Form';
import {useWhenChangedEffect} from '@netx/core-components/hooks/useWhenChangedEffect';
import type {Theme} from '@netx/core-components/Theme';
import {dispatcher} from '@netx/core-next/instance/dispatcher';
import {i18n} from '@netx/core/i18n';
import {Header, SideEditor, SingleFieldEditor} from '../';
import {
	useChangeStore,
	useGridStore,
	useProgressStore,
	useSelectionStore,
} from '../../store';

const styles = (theme: Theme) =>
	createStyles({
		root: {
			display: 'flex',
			flex: '1 1 auto',
			minHeight: 0,
			overflow: 'hidden',
			height: 'initial',
			position: 'relative',
			'&$overflownX': {
				'& .hypergrid-container': {
					'& .scrollbar-x-border-hack': {
						borderTop: `1px solid ${theme.palette.custom.border}`,
						display: 'block',
						height: '17px',
						left: 0,
						top: 'initial',
					},
					'& > div:not([class*="scrollbar-"]):not([class*="finbar-"])': {
						marginBottom: '17px',
					},
				},
			},
			'&$overflownY': {
				'& .hypergrid-container': {
					'& .scrollbar-y-border-hack': {
						borderLeft: `1px solid ${theme.palette.custom.border}`,
						display: 'block',
						width: '17px',
						left: 'initial',
						top: 0,
					},
					'& .scrollbar-x-border-hack': {
						right: '17px',
					},
					'& > div:not([class*="scrollbar-"]):not([class*="finbar-"])': {
						marginRight: '17px',
					},
				},
			},
			'& .hypergrid-container': {
				borderTop: `1px solid ${theme.palette.custom.border}`,
				height: 'initial',
				display: 'flex',
				flex: '1 1 auto',
				minHeight: 0,
				paddingBottom: 17,
				'& .container': {
					background: theme.palette.background.paper,
				},
				'& > div:not([class*="scrollbar-"]):not([class*="finbar-"])': {
					...theme.mixins.absoluteFill,
				},

				'& .scrollbar-x-border-hack, & .scrollbar-y-border-hack': {
					display: 'none',
					background:
						theme.palette.mode === 'light'
							? 'rgba(0, 0, 0, 0.05)'
							: 'rgba(255, 255, 255, 0.08)',
					bottom: 0,
					position: 'absolute',
					right: 0,
				},
				'& .finbar-horizontal, & .finbar-vertical': {
					'& .thumb': {
						opacity: 1,
						backgroundColor: theme.palette.mode === 'light' ? '#666' : '#bbb',
					},
				},
				'& .finbar-horizontal': {
					height: '11px',
					right: '16px !important',
				},
				'& .finbar-vertical': {
					width: '11px',
				},
			},
			'&$editorOpen': {
				'& $main': {
					pointerEvents: 'none',
					'&:before': {
						background: theme.palette.background.default,
						display: 'block',
						content: '"',
						opacity: 0.5,
						...theme.mixins.absoluteFill,
					},
				},
			},
		},
		main: {
			display: 'flex',
			flex: '1 1 auto',
			flexDirection: 'column',
			minHeight: 0,
		},
		sideEditor: {
			flex: '0 0 auto',
			marginTop: 4,
			position: 'relative',
		},
		editorOpen: {
			'&::after': {
				background: 'rgba(0, 0, 0, 0.5)',
				content: '""',
				display: 'block',
				...theme.mixins.absoluteFill,
			},
			'& $sideEditor': {
				zIndex: 2,
			},
		},
		closedActions: {
			flex: '1 1 auto',
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			position: 'absolute',
			top: 0,
			right: 0,
			bottom: 0,
			left: 0,
			pointerEvents: 'none',
		},
		overflownX: {},
		overflownY: {},
		progress: {
			position: 'absolute',
			top: 0,
			left: 0,
			right: 0,
		},
		wrapper: {
			display: 'flex',
			flex: '1 1 auto',
			flexDirection: 'column',
		},
		loadingOverlay: {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			background: 'rgba(255, 255, 255, 0.5)',
			...theme.mixins.absoluteFill,
		},
		loadingContainer: {
			padding: theme.spacing(2),
		},
		disable: {
			pointerEvents: 'none',
		},
		working: {},
	});

export interface GridEditorProps {
	onClose?: (event: any, reason: string) => void;
	onChange?: (hasChanges: boolean) => void;
	ref?: React.Ref<HTMLDivElement>;
}

const GridEditor = React.forwardRef<
	HTMLDivElement,
	WithStyles<typeof styles> & GridEditorProps
>(({classes, onChange, onClose}, ref) => {
	const rootRef = useRef<HTMLDivElement>(null);
	const [editorOpen, setEditorOpen] = useState(false);

	const confirm = useConfirmation();

	const grid = useGridStore();
	const selection = useSelectionStore();
	const changes = useChangeStore();
	const progress = useProgressStore();

	const hasChanges = !!changes.state.count;
	const singleRow = selection.state.inSingleRow;

	const disable = grid.state.loading || grid.state.saving;
	const working = progress.state.working || disable;
	const indeterminateProgress =
		(working && progress.state.progress === 0) ||
		(grid.state.saving && progress.state.progress === 100);

	useWhenChangedEffect(
		hasChanges,
		useCallback(
			(hasChanges) => {
				onChange?.(hasChanges);
			},
			[onChange],
		),
	);

	const handleClone = useCallback(() => {
		singleRow !== false &&
			dispatcher.trigger(
				'asset:tagPanel:grid:clone',
				grid.getCloneData(singleRow),
			);
	}, [grid.getCloneData, singleRow]);

	const handleClose = useCallback(() => {
		if (!hasChanges) return onClose?.(null, '');

		confirm({
			message: i18n.t('i18n.anyUnsavedChangesWillBeLostConfirm'),
		}).then(() => onClose?.(null, ''));
	}, [hasChanges, onClose]);

	const handleEditClick = useCallback(() => setEditorOpen(true), []);

	const handleInputLineSubmit = useCallback(
		async (data: any, changes: any, actions: any) =>
			grid.updateData(data, actions),
		[grid.updateData],
	);

	const handleSideEditorSubmit = useCallback(
		async (values: FormValues, actions: FormActions) => {
			await grid.updateData(values, actions);
			setEditorOpen(false);
		},
		[grid.updateData],
	);

	const _Header = useMemo(() => {
		return (
			<Header
				onClose={handleClose}
				onClone={handleClone}
				onEdit={handleEditClick}
			/>
		);
	}, [handleClose, handleClone, handleEditClick]);

	const _SideEditor = useMemo(() => {
		return (
			<div className={classes.sideEditor}>
				<SideEditor
					active={editorOpen}
					onSubmit={handleSideEditorSubmit}
					DrawerProps={{
						onCollapseToggle: setEditorOpen,
						overflowParent: rootRef,
					}}
				/>
			</div>
		);
	}, [classes, editorOpen, handleSideEditorSubmit, setEditorOpen]);

	const _SingleFieldEditor = useMemo(() => {
		return <SingleFieldEditor onSubmit={handleInputLineSubmit} />;
	}, [handleInputLineSubmit]);

	return useMemo(() => {
		return (
			<Paper
				ref={rootRef}
				className={clsx(classes.root, {
					[classes.working]: working,
					[classes.disable]: disable,
					[classes.editorOpen]: editorOpen,
					[classes.overflownX]: grid.state.overflownX,
					[classes.overflownY]: grid.state.overflownY,
				})}
			>
				<div className={classes.wrapper}>
					{_Header}

					<LinearProgress
						variant={indeterminateProgress ? 'indeterminate' : 'determinate'}
						key={working ? 0 : 1}
						value={
							grid.state.loading
								? undefined
								: working
								? progress.state.progress
								: 100
						}
						className={classes.progress}
					/>

					<div className={classes.main}>
						{_SingleFieldEditor}

						{/** Do not add any other props to these elements */}
						<div ref={ref}>
							<div className="scrollbar-x-border-hack" />
							<div className="scrollbar-y-border-hack" />
						</div>
					</div>
				</div>

				{_SideEditor}

				{/** Show dialog at initial data load */}
				{/* {grid.state.loading ? ( */}
				<Dialog
					title={i18n.t('i18n.loading')}
					disableEscapeKeyDown
					open={grid.state.loading}
					preventBackdropClose
					onClose={onClose}
				>
					<DialogContentText>
						{i18n.t('i18n.gridEditorDependingOnCartnSizeThisCouldTakeAMoment')}
					</DialogContentText>
				</Dialog>
				{/* ) : null} */}
			</Paper>
		);
	}, [
		classes,
		_Header,
		_SideEditor,
		_SingleFieldEditor,
		disable,
		indeterminateProgress,
		working,
		grid.state.loading,
		grid.state.overflownX,
		grid.state.overflownY,
		progress.state.progress,
	]);
});

export default withStyles(styles)(GridEditor);
